import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { IHashTag } from '../../types/hashtag';
import VerifyBadge from '../../components/verify-badge/verify-badge.component';
import {
  fetchLeaderboardApi,
  fetchTopDonatorApi,
  fetchTopFollowerApi,
  fetchTopInteractionsApi,
  fetchTopReceiversApi,
  TopInteractionItem,
} from '../../api/user-profile/leaderboard';
import {
  ITopDonator,
  ITopFollower,
  LeaderboardData,
} from '../../models/leaderboard.model';
import { useSelector } from 'react-redux';
import { RootState } from '../../configuration';
import _get from 'lodash/get';
import { ChevronDown, CircleCheck } from 'lucide-react';
import { fetchStatisticalByUserIdApi } from '../../api';
import { UserProfileStats } from '../../models';
import UkSlider from '../../components/slider/uk-slider.component';
import Avatar from '../../components/avatar/avatar.component';
import { handleFormatHastag } from '../../utils/formatHastag';
import { CustomDropdown } from '../../pages/user-setting/tabs/custom-dropdown';
import LiveDonateFeed from '../../components/live-donate/live-donate-feed';
import { periodRange } from '../../utils/honor-period';
import { hasHonorDisplayName, HonorTabKey } from '../../utils/honor-rows';
import { routePaths } from '../../routes/routes-path';

interface AsideRightProps {
  hashtags: IHashTag[];
  /**
   * Hiện khung "Donate trực tiếp" (và mở kết nối SignalR của nó). Mặc định false vì `AsideRight`
   * còn được dùng lại ở những trang không cần khung này (vd. /videos) — chỉ trang chủ mới bật.
   */
  showLiveDonate?: boolean;
}

export const AsideRight = ({ hashtags, showLiveDonate = false }: AsideRightProps) => {
  const [leaderboard, setLeaderboard] = useState<LeaderboardData>();
  const [statisticalUser, setStatisticalUser] = useState<UserProfileStats>();
  const navigator = useNavigate();
  const profileUser = useSelector(
    (state: RootState) => state.features.auth.profile
  );
  const handleTabClick = (tabName) => {
    setActiveTab(tabName);
    toggleDropdown();
  };

  const [isDropdownVisible, setIsDropdownVisible] = useState(false);

  const toggleDropdown = () => setIsDropdownVisible(!isDropdownVisible);

  useEffect(() => {
    const fetchLeaderboard = async () => {
      try {
        const response = await fetchLeaderboardApi();
        setLeaderboard(response);
      } catch (error) {
        console.error('Failed to fetch leaderboard:', error);
      }
    };

    fetchLeaderboard();
  }, []);

  useEffect(() => {
    const fetchStatisticalByUserId = async (id) => {
      try {
        const response = await fetchStatisticalByUserIdApi(id);
        setStatisticalUser(response);
      } catch (error) {
        console.error('Failed to fetch leaderboard:', error);
      }
    };

    // Trang chủ mount AsideRight ngay khi có token, trước khi fetchProfile() xong → chưa có userId
    // thì chưa gọi (tránh request `userid=undefined` và nháy số 0).
    if (!profileUser?.userId) return;
    fetchStatisticalByUserId(profileUser.userId);
  }, [profileUser?.userId]);

  const [month, setMonth] = useState<number>(new Date().getMonth() + 1);
  const [activeTab, setActiveTab] = useState('followers');
  const [isCurrent, setIsCurrent] = useState<boolean>(true);
  const [topReceivers, setTopReceivers] = useState<ITopDonator>({
    totalItems: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPreviousPage: false,
    items: [],
  });
  const [topDonator, setTopDonator] = useState<ITopDonator>({
    totalItems: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPreviousPage: false,
    items: [],
  });
  const [topFollower, setTopFollower] = useState<ITopFollower>({
    total: 0,
    hasNext: false,
    data: [],
  });
  const [topInteractions, setTopInteractions] = useState<TopInteractionItem[]>([]);

  // Khoá tab của khung "Bảng xếp hạng" trang chủ -> khoá tab tương ứng trên /vinh-danh.
  const HONOR_TAB_BY_ACTIVE_TAB: Record<string, HonorTabKey> = {
    donator: 'donate',
    followers: 'theo-doi',
    receives: 'nhan-donate',
    interactions: 'tuong-tac',
  };

  // "Theo tháng" + tháng M của năm hiện tại -> mốc tính theo giờ VN, dựng từ
  // periodRange('month', ...) để không lặp lại logic đầu/cuối kỳ (sửa lỗi ngày cuối
  // tháng bị bỏ sót của cách tính cũ dùng dayjs `endOf('month')` dạng YYYY-MM-DD).
  const getMonthRange = (m: number) => {
    const currentYear = new Date().getFullYear();
    const { startTime, endTime } = periodRange('month', new Date(Date.UTC(currentYear, m - 1, 15)));
    return { startTime, endTime };
  };

  useEffect(() => {
    const fetchTopDonator = async (params?: {
      startTime?: string;
      endTime?: string;
    }) => {
      try {
        const responses = await fetchTopDonatorApi(params);
        setTopDonator(responses);
      } catch (error) {
        console.error('Failed to fetch leaderboard:', error);
      }
    };
    if (activeTab === 'donator') {
      if (isCurrent) {
        fetchTopDonator();
      } else {
        fetchTopDonator(getMonthRange(month));
      }
    }

    const fetchFollower = async (params?: {
      startTime?: string;
      endTime?: string;
    }) => {
      try {
        const responses = await fetchTopFollowerApi(params);
        setTopFollower(responses);
      } catch (error) {
        console.error('Failed to fetch leaderboard:', error);
      }
    };
    if (activeTab === 'followers') {
      if (isCurrent) {
        fetchFollower();
      } else {
        fetchFollower(getMonthRange(month));
      }
    }

    const fetchReceivers = async (params?: {
      startTime?: string;
      endTime?: string;
    }) => {
      try {
        const responses = await fetchTopReceiversApi(params);
        setTopReceivers(responses);
      } catch (error) {
        console.error('Failed to fetch leaderboard:', error);
      }
    };
    if (activeTab === 'receives') {
      if (isCurrent) {
        fetchReceivers();
      } else {
        fetchReceivers(getMonthRange(month));
      }
    }

    const fetchInteractions = async (params?: {
      startTime?: string;
      endTime?: string;
    }) => {
      try {
        const responses = await fetchTopInteractionsApi({
          ...params,
          take: 20,
        });
        setTopInteractions(responses);
      } catch (error) {
        console.error('Failed to fetch leaderboard:', error);
      }
    };
    if (activeTab === 'interactions') {
      if (isCurrent) {
        fetchInteractions();
      } else {
        fetchInteractions(getMonthRange(month));
      }
    }
  }, [isCurrent, activeTab, month]);

  return (
    <aside
      className="hidden lg:block flex-1 sticky top-[84px] overflow-auto no-scrollbar"
      style={{
        height: 'calc(100vh - 84px)',
      }}
    >
      <div className="space-y-4 pb-6">
        <section>
          <div className="border rounded-xl border-[#DEE0E6] dark:border-slate-700 p-3   dark:bg-dark2 dark:border-slate-700">
            <div className="flex  border-b border-b-[#DEE0E6] dark:border-b-slate-700 px-3 py-2 items-start gap-2 bo dark:border-b-slate-700">
              <Link to="/profile" className="shrink-0">
                <Avatar
                  avatar={_get(profileUser, 'avatar', '')}
                  label={_get(profileUser, 'requestFullName', '')}
                  size="sm"
                />
              </Link>
              <div className="flex flex-col gap-1">
                <h2 className="font-semibold flex justify-center text-sm items-center gap-2 dark:text-white">
                  <Link to="/profile" className="hover:underline">
                    {profileUser?.fullname}
                  </Link>
                  {profileUser && (
                    <VerifyBadge userType={profileUser?.userType} />
                  )}
                  {/* {_get(profileUser, 'userType') === 'CONTENT_CREATOR' ? (
                    <CircleCheck color="green" size={16} />
                  ) : (
                    profileUser?.isVerifyEmail && (
                      <CircleCheck color="black" size={16} />
                    )
                  )} */}
                </h2>
                <p className="text-[#080809] dark:text-slate-300 font-medium text-xs truncate ">
                  {profileUser?.description}
                </p>
                <Link
                  to="/profile"
                  className="text-[#0071A6] font-medium text-xs w-fit hover:underline"
                >
                  @{profileUser?.username}
                </Link>
              </div>
            </div>

            <ul className="flex flex-col mt-[10px] gap-[10px] w-full">
              <li className="border-b border-b-[#DEE0E6] dark:border-b-slate-700 last:border-b-0 px-3 py-2 w-full">
                <h4 className="font-normal  text-base text-[#111827] dark:text-white flex items-center gap-1">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M14.6467 1.55317C14.1667 1.0065 13.4533 0.666504 12.6667 0.666504C11.92 0.666504 11.24 0.973171 10.7533 1.47317C10.4733 1.75984 10.26 2.1065 10.1333 2.49317C10.0467 2.75984 10 3.03984 10 3.33317C10 3.83317 10.14 4.3065 10.3867 4.7065C10.52 4.93317 10.6933 5.13984 10.8933 5.31317C11.36 5.73984 11.98 5.99984 12.6667 5.99984C12.96 5.99984 13.24 5.95317 13.5 5.85984C14.1133 5.6665 14.6267 5.2465 14.9467 4.7065C15.0867 4.47984 15.1933 4.21984 15.2533 3.95317C15.3067 3.75317 15.3333 3.5465 15.3333 3.33317C15.3333 2.65317 15.0733 2.0265 14.6467 1.55317ZM13.66 3.81984H13.1667V4.33984C13.1667 4.61317 12.94 4.83984 12.6667 4.83984C12.3933 4.83984 12.1667 4.61317 12.1667 4.33984V3.81984H11.6733C11.4 3.81984 11.1733 3.59317 11.1733 3.31984C11.1733 3.0465 11.4 2.81984 11.6733 2.81984H12.1667V2.3465C12.1667 2.07317 12.3933 1.8465 12.6667 1.8465C12.94 1.8465 13.1667 2.07317 13.1667 2.3465V2.81984H13.66C13.9333 2.81984 14.16 3.0465 14.16 3.31984C14.16 3.59317 13.94 3.81984 13.66 3.81984Z"
                      fill="#657384"
                    />
                    <path
                      d="M14.6673 8.00016C14.6673 7.12683 14.5007 6.28683 14.1873 5.52016C13.9807 5.66683 13.7473 5.78016 13.5007 5.86016C13.4273 5.88683 13.354 5.90683 13.274 5.92683C13.5273 6.56683 13.6673 7.26683 13.6673 8.00016C13.6673 9.54683 13.0407 10.9535 12.0273 11.9802C11.834 11.7335 11.5873 11.5068 11.294 11.3135C9.48732 10.1002 6.52732 10.1002 4.70732 11.3135C4.41398 11.5068 4.17398 11.7335 3.97398 11.9802C2.96065 10.9535 2.33398 9.54683 2.33398 8.00016C2.33398 4.8735 4.87398 2.3335 8.00065 2.3335C8.72732 2.3335 9.42732 2.4735 10.0673 2.72683C10.0873 2.64683 10.1073 2.5735 10.134 2.4935C10.214 2.24683 10.3273 2.02016 10.4807 1.8135C9.71398 1.50016 8.87398 1.3335 8.00065 1.3335C4.32732 1.3335 1.33398 4.32683 1.33398 8.00016C1.33398 9.9335 2.16732 11.6735 3.48732 12.8935C3.48732 12.9002 3.48732 12.9002 3.48065 12.9068C3.54732 12.9735 3.62732 13.0268 3.69398 13.0868C3.73398 13.1202 3.76732 13.1535 3.80732 13.1802C3.92732 13.2802 4.06065 13.3735 4.18732 13.4668C4.23398 13.5002 4.27398 13.5268 4.32065 13.5602C4.44732 13.6468 4.58065 13.7268 4.72065 13.8002C4.76732 13.8268 4.82065 13.8602 4.86732 13.8868C5.00065 13.9602 5.14065 14.0268 5.28732 14.0868C5.34065 14.1135 5.39398 14.1402 5.44732 14.1602C5.59398 14.2202 5.74065 14.2735 5.88732 14.3202C5.94065 14.3402 5.99398 14.3602 6.04732 14.3735C6.20732 14.4202 6.36732 14.4602 6.52732 14.5002C6.57398 14.5135 6.62065 14.5268 6.67398 14.5335C6.86065 14.5735 7.04732 14.6002 7.24065 14.6202C7.26732 14.6202 7.29398 14.6268 7.32065 14.6335C7.54732 14.6535 7.77398 14.6668 8.00065 14.6668C8.22732 14.6668 8.45398 14.6535 8.67398 14.6335C8.70065 14.6335 8.72732 14.6268 8.75398 14.6202C8.94732 14.6002 9.13398 14.5735 9.32065 14.5335C9.36732 14.5268 9.41398 14.5068 9.46732 14.5002C9.62732 14.4602 9.79398 14.4268 9.94732 14.3735C10.0007 14.3535 10.054 14.3335 10.1073 14.3202C10.254 14.2668 10.4073 14.2202 10.5473 14.1602C10.6007 14.1402 10.654 14.1135 10.7073 14.0868C10.8473 14.0268 10.9873 13.9602 11.1273 13.8868C11.1807 13.8602 11.2273 13.8268 11.274 13.8002C11.4073 13.7202 11.5407 13.6468 11.674 13.5602C11.7207 13.5335 11.7607 13.5002 11.8073 13.4668C11.9407 13.3735 12.0673 13.2802 12.1873 13.1802C12.2273 13.1468 12.2607 13.1135 12.3007 13.0868C12.374 13.0268 12.4473 12.9668 12.514 12.9068C12.514 12.9002 12.514 12.9002 12.5073 12.8935C13.834 11.6735 14.6673 9.9335 14.6673 8.00016Z"
                      fill="#657384"
                    />
                    <path
                      d="M8 4.62012C6.62 4.62012 5.5 5.74012 5.5 7.12012C5.5 8.47345 6.56 9.57345 7.96667 9.61345C7.98667 9.61345 8.01333 9.61345 8.02667 9.61345C8.04 9.61345 8.06 9.61345 8.07333 9.61345C8.08 9.61345 8.08667 9.61345 8.08667 9.61345C9.43333 9.56678 10.4933 8.47345 10.5 7.12012C10.5 5.74012 9.38 4.62012 8 4.62012Z"
                      fill="#657384"
                    />
                  </svg>
                  {statisticalUser ? statisticalUser.newFriend || 0 : '–'}
                  <span className="text-sm font-semibold  block">Bạn bè</span>
                </h4>
              </li>
              <li className="border-b border-b-[#DEE0E6] dark:border-b-slate-700 last:border-b-0 px-3 py-2 w-full">
                <h4 className="font-normal  text-base text-[#111827] dark:text-white flex items-center gap-1">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M9.95327 1.66668V5.60001C9.95327 5.89334 9.6066 6.04001 9.39327 5.84668L8.2266 4.77334C8.09993 4.65334 7.89993 4.65334 7.77327 4.77334L6.6066 5.84001C6.39327 6.04001 6.0466 5.88668 6.0466 5.60001V1.66668C6.0466 1.48001 6.19327 1.33334 6.37993 1.33334H9.61993C9.8066 1.33334 9.95327 1.48001 9.95327 1.66668Z"
                      fill="#657384"
                    />
                    <path
                      d="M11.3199 1.37333C11.1266 1.34666 10.9533 1.51333 10.9533 1.70666V5.72C10.9533 6.22666 10.6533 6.68666 10.1866 6.89333C9.71994 7.09333 9.17994 7.00666 8.80661 6.66L8.22661 6.12666C8.09994 6.00666 7.90661 6.00666 7.77327 6.12666L7.19327 6.66C6.95327 6.88666 6.63994 7 6.32661 7C6.15327 7 5.97994 6.96666 5.81327 6.89333C5.34661 6.68666 5.04661 6.22666 5.04661 5.72V1.70666C5.04661 1.51333 4.87327 1.34666 4.67994 1.37333C2.81327 1.60666 1.99994 2.86666 1.99994 4.66666V11.3333C1.99994 13.3333 2.99994 14.6667 5.33327 14.6667H10.6666C12.9999 14.6667 13.9999 13.3333 13.9999 11.3333V4.66666C13.9999 2.86666 13.1866 1.60666 11.3199 1.37333ZM11.6666 12.5H5.99994C5.72661 12.5 5.49994 12.2733 5.49994 12C5.49994 11.7267 5.72661 11.5 5.99994 11.5H11.6666C11.9399 11.5 12.1666 11.7267 12.1666 12C12.1666 12.2733 11.9399 12.5 11.6666 12.5ZM11.6666 9.83333H8.83327C8.55994 9.83333 8.33327 9.60666 8.33327 9.33333C8.33327 9.06 8.55994 8.83333 8.83327 8.83333H11.6666C11.9399 8.83333 12.1666 9.06 12.1666 9.33333C12.1666 9.60666 11.9399 9.83333 11.6666 9.83333Z"
                      fill="#657384"
                    />
                  </svg>

                  {statisticalUser ? statisticalUser.post || 0 : '–'}
                  <span className="text-sm font-semibold  block">Bài viết</span>
                </h4>
              </li>
              <li className="border-b border-b-[#DEE0E6] dark:border-b-slate-700 last:border-b-0 px-3 py-2 w-full">
                <h4 className="font-normal  text-base text-[#111827] dark:text-white flex items-center gap-1">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M5.59375 12.3265V5.55315C5.59375 5.28649 5.67375 5.02649 5.82042 4.80649L7.64042 2.09982C7.92708 1.66649 8.64042 1.35982 9.24708 1.58649C9.90042 1.80649 10.3337 2.53982 10.1937 3.19315L9.84708 5.37315C9.82042 5.57315 9.87375 5.75315 9.98708 5.89315C10.1004 6.01982 10.2671 6.09982 10.4471 6.09982H13.1871C13.7137 6.09982 14.1671 6.31315 14.4337 6.68649C14.6871 7.04649 14.7337 7.51315 14.5671 7.98649L12.9271 12.9798C12.7204 13.8065 11.8204 14.4798 10.9271 14.4798H8.32708C7.88042 14.4798 7.25375 14.3265 6.96708 14.0398L6.11375 13.3798C5.78708 13.1332 5.59375 12.7398 5.59375 12.3265Z"
                      fill="#657384"
                    />
                    <path
                      d="M3.47398 4.25293H2.78732C1.75398 4.25293 1.33398 4.65293 1.33398 5.6396V12.3463C1.33398 13.3329 1.75398 13.7329 2.78732 13.7329H3.47398C4.50732 13.7329 4.92732 13.3329 4.92732 12.3463V5.6396C4.92732 4.65293 4.50732 4.25293 3.47398 4.25293Z"
                      fill="#657384"
                    />
                  </svg>
                  {statisticalUser ? statisticalUser.like || 0 : '–'}
                  <span className="text-sm font-semibold  block">Like</span>
                </h4>
              </li>
              <li className="border-b border-b-[#DEE0E6] dark:border-b-slate-700 last:border-b-0 px-3 py-2 w-full">
                <h4 className="font-normal  text-base text-[#111827] dark:text-white flex items-center gap-1">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M11.3333 1.33334H4.6666C2.8266 1.33334 1.33327 2.82001 1.33327 4.65334V8.64001V9.30668C1.33327 11.14 2.8266 12.6267 4.6666 12.6267H5.6666C5.8466 12.6267 6.0866 12.7467 6.19993 12.8933L7.19993 14.22C7.63993 14.8067 8.35993 14.8067 8.79993 14.22L9.79993 12.8933C9.9266 12.7267 10.1266 12.6267 10.3333 12.6267H11.3333C13.1733 12.6267 14.6666 11.14 14.6666 9.30668V4.65334C14.6666 2.82001 13.1733 1.33334 11.3333 1.33334ZM8.6666 9.16668H4.6666C4.39327 9.16668 4.1666 8.94001 4.1666 8.66668C4.1666 8.39334 4.39327 8.16668 4.6666 8.16668H8.6666C8.93993 8.16668 9.1666 8.39334 9.1666 8.66668C9.1666 8.94001 8.93993 9.16668 8.6666 9.16668ZM11.3333 5.83334H4.6666C4.39327 5.83334 4.1666 5.60668 4.1666 5.33334C4.1666 5.06001 4.39327 4.83334 4.6666 4.83334H11.3333C11.6066 4.83334 11.8333 5.06001 11.8333 5.33334C11.8333 5.60668 11.6066 5.83334 11.3333 5.83334Z"
                      fill="#657384"
                    />
                  </svg>
                  {statisticalUser ? statisticalUser.comment || 0 : '–'}
                  <span className="text-sm font-semibold  block">
                    Bình luận
                  </span>
                </h4>
              </li>
            </ul>
          </div>
        </section>
        {showLiveDonate && <LiveDonateFeed />}
        <section>
          <div className="ui-card overflow-hidden">
            <div className="border-b border-[#E5E7EB] px-4 py-3 dark:border-slate-700">
              <h3 className="text-sm font-bold ui-heading">Bảng xếp hạng</h3>
            </div>
            <div className="flex border-b border-[#E5E7EB] dark:border-slate-700">
              {[
                { key: 'donator', label: 'Donate' },
                { key: 'followers', label: 'Theo dõi' },
                { key: 'receives', label: 'Nhận donate' },
                { key: 'interactions', label: 'Tương tác' },
              ].map((t) => (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => handleTabClick(t.key)}
                  className={`relative flex-1 px-2 py-2.5 text-xs font-semibold transition-colors ${
                    activeTab === t.key
                      ? 'text-primary'
                      : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200'
                  }`}
                >
                  {t.label}
                  {activeTab === t.key && (
                    <span className="absolute inset-x-2 bottom-0 h-0.5 rounded-full bg-primary" />
                  )}
                </button>
              ))}
            </div>
            <div className="p-3">
              <div className="mb-3 flex gap-1 rounded-lg bg-slate-100 p-1 dark:bg-slate-800">
                <button
                  type="button"
                  onClick={() => setIsCurrent(true)}
                  className={`flex-1 rounded-md py-1.5 text-xs font-semibold transition-colors ${
                    isCurrent
                      ? 'bg-white text-primary shadow-sm dark:bg-slate-700'
                      : 'text-slate-500 dark:text-slate-400'
                  }`}
                >
                  Hiện tại
                </button>
                <button
                  type="button"
                  onClick={() => setIsCurrent(false)}
                  className={`flex-1 rounded-md py-1.5 text-xs font-semibold transition-colors ${
                    !isCurrent
                      ? 'bg-white text-primary shadow-sm dark:bg-slate-700'
                      : 'text-slate-500 dark:text-slate-400'
                  }`}
                >
                  Theo tháng
                </button>
              </div>
              {!isCurrent && (
                <div className="mb-3">
                  <CustomDropdown
                    disable={isCurrent}
                    value={month.toString()}
                    options={Array.from({ length: 12 }, (_, idx) => ({
                      label: `Tháng ${idx + 1}`,
                      value: `${idx + 1}`,
                    }))}
                    placeholder="Tháng"
                    customClassName="py-2 h-10 px-3 text-sm font-semibold"
                    onChange={(val) => setMonth(Number(val))}
                  />
                </div>
              )}
              {activeTab === 'donator' && (
                <UkSlider sliderItems={topDonator.items.filter(hasHonorDisplayName).slice(0, 5)} />
              )}
              {activeTab === 'followers' && (
                <UkSlider sliderItems={topFollower.data.filter(hasHonorDisplayName).slice(0, 5)} />
              )}
              {activeTab === 'receives' && (
                <UkSlider sliderItems={topReceivers.items.filter(hasHonorDisplayName).slice(0, 5)} />
              )}
              {activeTab === 'interactions' && (
                <UkSlider sliderItems={topInteractions.filter(hasHonorDisplayName).slice(0, 5)} />
              )}
              <button
                type="button"
                onClick={() => {
                  const tab = HONOR_TAB_BY_ACTIVE_TAB[activeTab] ?? 'tuong-tac';
                  // Trang /vinh-danh không có chế độ xem theo 1 tháng cụ thể trong quá khứ - chỉ có
                  // "Tháng này" (ky=thang) và "Mọi thời gian" (ky=tat-ca). Nên "Theo tháng" chỉ map
                  // sang ky=thang khi đúng tháng hiện tại, còn lại (tháng khác) đành trả về tat-ca.
                  const isSelectedMonthCurrent = month === new Date().getMonth() + 1;
                  const ky = isCurrent || !isSelectedMonthCurrent ? 'tat-ca' : 'thang';
                  navigator(`${routePaths.honor}?tab=${tab}&ky=${ky}`);
                }}
                className="mt-2 w-full rounded-lg py-2 text-xs font-semibold text-primary transition-colors hover:bg-primary/5"
              >
                Xem tất cả
              </button>
            </div>
          </div>
        </section>
      </div>
    </aside>
  );
};
